<script setup lang="ts">
    import { useToasteo } from '@deegital/vue-trustup-io-toasteo';
    import usePinchZoom from '~/composables/usePinchZoom';
    import { ModelType } from '~/app/base/schemas/BaseSchema';
    import EndpointFactory from '~/app/factories/EndpointFactory';
    import InvoiceForm from '~/app/invoicing/classes/InvoiceForm';
    import InvoiceEndpoint from '~/app/invoicing/endpoints/InvoiceEndpoint';
    import Invoice from '~/app/invoicing/models/Invoice';
    import { useIsMobileLayout } from '~/composables/useIsMobileLayout';

    // The desktop rendering of this sheet (via UiResponsiveModal → Modal.vue) has no
    // built-in title, unlike UiActionSheet's mobile rendering — so the title stays
    // here, just hidden on mobile where the sheet chrome already shows it inline
    // with the close button.
    const isMobile = useIsMobileLayout();

    interface PayloadInterface {
        invoice: Invoice;
        form: InvoiceForm<any>;
    }

    // Pinch-zoom on the document alone (mobile): the frame below clips it, and the
    // gesture only ever moves what is inside that frame. Untouched, the document fills
    // the frame exactly, so the preview looks as it did before (`.preview-zoom-frame`).
    const zoomViewport = ref<HTMLElement | null>(null);
    const zoomContent = ref<HTMLElement | null>(null);
    // Desktop hands the composable nothing, so it binds no listener and returns the
    // identity transform — the preview there is the untouched one.
    const zoomTarget = computed(() => (isMobile.value ? zoomViewport.value : null));
    const { isZoomed, contentStyle, viewportStyle, reset } = usePinchZoom(zoomTarget, zoomContent);
    const zoomViewportStyle = computed(() => (isMobile.value ? viewportStyle.value : undefined));
    const zoomContentStyle = computed(() => (isMobile.value ? contentStyle.value : undefined));

    const { t } = useTranslation();
    const toasteo = useToasteo();
    const loading = ref(true);
    const generatingPdf = ref(false);
    const payload = ref<PayloadInterface>();
    const html = ref('');
    const endpoint = EndpointFactory.make(ModelType.INVOICES) as InvoiceEndpoint;
    const { trackPreviewed } = useInvoiceTracking();

    useModal(
        'invoicing:invoice:preview-html',
        async (p: PayloadInterface) => {
            payload.value = p;
            const response = await endpoint.previewHtml(payload.value.form.toSchema(), payload.value.invoice?.getId());
            html.value = response.html;
            loading.value = false;
            trackPreviewed(payload.value.form.documentType, 'form');
        },
        () => {
            loading.value = true;
            html.value = '';
        }
    );

    const generatePdf = async (p: LoadingButtonClickedEventPayload) => {
        const response = await p.loadUntil(endpoint.preview(payload.value.form.toSchema()));
        if (response.data && response.data.url) {
            window.open(response.data.url, '_blank');
            // TODO: Add a onClick callback to the toast
            toasteo.success({
                title: t('toasts.preview_generated'),
                text: t('toasts.popup_helper'),
                // text: 'Click here if it does not open. (t)',
                // showUndo: false,
                // callback: () => {
                //     window.open(response.pdf, '_blank');
                // }
            });
        }
    };
</script>

<template>
    <UiResponsiveModal
        ref="modal"
        modal-style="right_panel_pdf"
        name="invoicing:invoice:preview-html"
        :width="800"
        :title="$t('invoicing.invoice_preview_html_modal.title')"
        :max-height-percent="92"
    >
        <!-- While the preview is fetched the sheet must already be the size it will
             settle at. It used to render a bare `Loader`: the drawer's own slide-in
             (500ms) therefore animated an 81px box still below the fold, finished long
             before the HTML arrived, and the real panel then appeared by jumping from
             81px to its full height in a single frame — the open read as instantaneous
             rather than as a drawer rising.

             So the loading state reserves the document's own box: the same A4 ratio
             `preview-zoom-frame` gives the loaded frame, inside the same padding. The
             sheet opens at its final height and the slide is the one the other drawers
             have. `min-h-svh` on top of the frame: the loaded panel also carries a hint
             row, a note and the download button, and the sheet ends up against its own
             `maxHeightPercent` cap — asking for a full screen lets that cap decide the
             height instead of repeating its value here. Mobile only — on desktop the modal is centred and fixed-width, its
             height never drove an entrance animation, so it keeps the plain loader. -->
        <div
            v-if="loading && isMobile"
            :class="[$theme('modal.padding'), 'min-h-svh']"
        >
            <div class="mb-12">
                <div class="preview-zoom-frame relative mb-2 flex items-center justify-center overflow-hidden rounded border border-gray-200 bg-white">
                    <Loader />
                </div>
            </div>
        </div>
        <Loader v-else-if="loading" />
        <template v-else>
            <div
                v-if="!isMobile"
                :class="$theme('modal.title.container')"
            >
                <h2 :class="$theme('modal.title.text')">
                    {{ $t('invoicing.invoice_preview_html_modal.title') }}
                </h2>
            </div>
            <div :class="$theme('modal.padding')">
                <div class="mb-12">
                    <!-- Mobile only: the document becomes its own zoom surface (see
                         `usePinchZoom`). Desktop keeps the plain fitted preview and the
                         browser's page zoom, so nothing here is wrapped or transformed
                         for it — the ref, the styles and the frame are all `isMobile`. -->
                    <div
                        ref="zoomViewport"
                        :class="isMobile ? 'preview-zoom-frame relative mb-2 overflow-hidden rounded border border-gray-200 bg-white' : ''"
                        :style="zoomViewportStyle"
                    >
                        <div
                            ref="zoomContent"
                            :style="zoomContentStyle"
                        >
                            <div class="preview-scale-container">
                                <div
                                    class="preview-scale-box mx-auto border border-gray-200 rounded bg-white overflow-hidden"
                                    :class="isMobile ? '' : 'mb-4'"
                                >
                                    <div class="preview-scale-content">
                                        <!-- `pointer-events-none` is what makes the pinch
                                             reachable at all: touch and gesture events
                                             raised inside an iframe stay in its own
                                             document and never reach this one, so with a
                                             live iframe covering the whole surface the
                                             handlers below it never fired and Safari fell
                                             back to zooming the page. The document is a
                                             read-only render, so nothing is lost by making
                                             it transparent to the finger. Mobile only —
                                             desktop never installs the gesture. -->
                                        <iframe
                                            :srcdoc="html"
                                            class="preview-scale-iframe"
                                            :class="isMobile ? 'pointer-events-none' : ''"
                                        ></iframe>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- The gesture is invisible until tried, and the reset is the only
                         way back that does not need a second precise pinch. -->
                    <div
                        v-if="isMobile"
                        class="mb-4 flex items-center justify-between gap-2"
                    >
                        <span class="text-[11px] text-zinc-500">{{ $t('invoicing.invoice_preview_html_modal.zoom_hint') }}</span>
                        <button
                            v-if="isZoomed"
                            type="button"
                            class="shrink-0 text-[11px] font-medium text-zinc-700 underline"
                            @click="reset"
                        >
                            {{ $t('actions.reset') }}
                        </button>
                    </div>
                    <p class="text-xs text-gray-600">
                        {{ $t('invoicing.invoice_preview_html_modal.notes') }}
                        <span v-if="payload.invoice?.frenchVatCertificateUrl">
                            {{ $t('invoicing.invoice_preview_html_modal.french_vat_certificate_notes') }}
                        </span>
                    </p>
                </div>
                <div class="flex justify-center mb-8">
                    <LoadingButton
                        :loading="generatingPdf"
                        @clicked="generatePdf"
                    >
                        {{ $t('invoicing.invoice_preview_html_modal.generate_pdf') }}
                    </LoadingButton>
                </div>
            </div>
        </template>
    </UiResponsiveModal>
</template>

<style scoped>
    /* The iframe renders the invoice template at its real, fixed A4-at-96dpi layout
       size (794x1122) so it looks exactly like the exported PDF. `preview-scale-container`
       establishes a container-query context sized to the actual available width (the modal's
       content column, not the viewport), and `100cqw / 794px` — a length divided by a length —
       resolves to the unitless shrink ratio `scale()` needs. `min(1, ...)` never scales up on
       wide screens, so desktop is pixel-identical to the unscaled original. */
    .preview-scale-container {
        container-type: inline-size;
    }

    .preview-scale-box {
        width: min(794px, 100cqw);
        aspect-ratio: 794 / 1122;
    }

    .preview-scale-content,
    .preview-scale-iframe {
        width: 794px;
        height: 1122px;
    }

    .preview-scale-content {
        transform: scale(min(1, calc(100cqw / 794px)));
        transform-origin: top left;
    }

    /* Mobile zoom frame. Its height comes from the document's own A4 ratio rather than
       from measuring the content: the content is what the frame clips, so measuring it
       to size the frame is a loop waiting to settle — and it never did, the frame stayed
       at zero. The ratio is the same one `.preview-scale-box` is built on, so at rest the
       document fills the frame exactly and the preview looks untouched. */
    .preview-zoom-frame {
        aspect-ratio: 794 / 1122;
    }
</style>
